import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal, Space, Typography, message } from 'antd'
import { DeleteOutlined, DownloadOutlined, EditOutlined, SendOutlined } from '@ant-design/icons'
import {
  updateVideoAsset,
  videoAssetPlaybackUrl,
  videoAssetPosterUrl,
  type VideoAssetItem,
} from '../api'

function formatDuration(seconds: number | null): string {
  if (seconds == null || Number.isNaN(seconds)) return '—'
  const total = Math.max(0, Math.round(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatResolution(w: number | null, h: number | null): string {
  if (!w || !h) return '—'
  return `${w} × ${h}`
}

function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

/** Video Preview Modal — spec §十. Native `<video controls>` supplies
 * pause/timeline/volume/fullscreen for free (spec §十: "不要重新实现浏览器原
 * 生的 video player 能解决的基础播放能力"); this component only adds the
 * chrome around it (metadata + 下载/编辑信息/用于内容发布/删除). */
export default function VideoAssetPreviewModal({
  item,
  workspaceId,
  onClose,
  onDelete,
  onUpdated,
  onPublish,
}: {
  item: VideoAssetItem | null
  workspaceId: string
  onClose: () => void
  /** Runs the SAME confirm+delete-API+toast flow as the grid/list row
   * delete button (passed in by the page) — this modal never re-implements
   * delete semantics of its own. */
  onDelete: (item: VideoAssetItem) => void
  onUpdated: () => void
  onPublish: (item: VideoAssetItem) => void | Promise<void>
}) {
  const { t } = useTranslation(['video', 'actions'])
  const [editing, setEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [playbackError, setPlaybackError] = useState(false)

  useEffect(() => {
    setEditing(false)
    setPlaybackError(false)
    if (item) {
      setDraftTitle(item.title || '')
      setDraftDescription(item.description || '')
    }
  }, [item])

  if (!item) return null

  const src = videoAssetPlaybackUrl(workspaceId, item)
  const poster = videoAssetPosterUrl(workspaceId, item)

  const onSaveEdit = async () => {
    setSaving(true)
    try {
      await updateVideoAsset(workspaceId, item.id, {
        title: draftTitle.trim() || undefined,
        description: draftDescription.trim() || undefined,
      })
      message.success(t('video:assets.previewModal.updated'))
      setEditing(false)
      onUpdated()
    } catch (e) {
      message.error((e as Error).message || t('video:assets.previewModal.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={!!item}
      title={item.title}
      onCancel={onClose}
      width={760}
      centered
      className="vac-preview-modal"
      destroyOnClose
      maskClosable
      styles={{ body: { maxHeight: 'min(80vh, 760px)', overflow: 'auto' } }}
      footer={
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => window.open(videoAssetPlaybackUrl(workspaceId, item, true), '_blank', 'noopener')}>
            {t('actions:download')}
          </Button>
          {item.can_edit_meta && (
            <Button icon={<EditOutlined />} onClick={() => setEditing((v) => !v)}>
              {t('video:assets.previewModal.editMeta')}
            </Button>
          )}
          <Button
            icon={<SendOutlined />}
            disabled={!item.can_publish}
            title={item.can_publish ? undefined : t('video:assets.actions.publishDisabled')}
            onClick={() => void onPublish(item)}
          >
            {t('video:assets.actions.useForPublish')}
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(item)}>
            {t('actions:delete')}
          </Button>
          <Button onClick={onClose}>{t('actions:close')}</Button>
        </Space>
      }
    >
      <div className="vac-preview-body">
        <div className="vac-preview-stage">
          {playbackError ? (
            <div className="vac-preview-error">{t('video:assets.previewModal.playbackError')}</div>
          ) : (
            <video controls poster={poster} src={src} onError={() => setPlaybackError(true)} />
          )}
        </div>

        {editing ? (
          <div className="vac-preview-edit">
            <Input
              placeholder={t('video:assets.previewModal.titlePlaceholder')}
              value={draftTitle}
              maxLength={500}
              onChange={(e) => setDraftTitle(e.target.value)}
            />
            <Input.TextArea
              placeholder={t('video:assets.previewModal.descriptionPlaceholder')}
              value={draftDescription}
              maxLength={4000}
              rows={3}
              onChange={(e) => setDraftDescription(e.target.value)}
            />
            <Space>
              <Button type="primary" loading={saving} onClick={() => void onSaveEdit()}>
                {t('actions:save')}
              </Button>
              <Button onClick={() => setEditing(false)} disabled={saving}>
                {t('actions:cancel')}
              </Button>
            </Space>
          </div>
        ) : (
          <dl className="vac-preview-meta">
            <div>
              <dt>{t('video:assets.previewModal.duration')}</dt>
              <dd>{formatDuration(item.duration_seconds)}</dd>
            </div>
            <div>
              <dt>{t('video:assets.previewModal.resolution')}</dt>
              <dd>{formatResolution(item.width, item.height)}</dd>
            </div>
            <div>
              <dt>{t('video:assets.previewModal.fileSize')}</dt>
              <dd>{formatBytes(item.size_bytes)}</dd>
            </div>
            <div>
              <dt>{t('video:assets.previewModal.type')}</dt>
              <dd>{t(`video:assets.sourceKinds.${item.source_kind}`)}</dd>
            </div>
            <div>
              <dt>{t('video:assets.previewModal.createdAt')}</dt>
              <dd>{formatAbsoluteTime(item.created_at)}</dd>
            </div>
            <div>
              <dt>{t('video:assets.previewModal.publishStatus')}</dt>
              <dd>{item.is_published ? t('video:assets.previewModal.published') : t('video:assets.previewModal.unpublished')}</dd>
            </div>
            {item.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <dt>{t('video:assets.previewModal.description')}</dt>
                <dd>
                  <Typography.Text>{item.description}</Typography.Text>
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </Modal>
  )
}
